import cyberpunk from "../../../apis/cyberpunk"
import history from "../../../history"
// This is a duck
// https://github.com/erikras/ducks-modular-redux

// Actions
const SIGNUP = "cyberpunk-media/SIGNUP"
const SIGNUP_REQUEST = "cyberpunk-media/SIGNUP_REQUEST"
const SIGNUP_ERROR = "cyberpunk-media/SIGNUP_ERROR"
const SIGNUP_CLEAR_ERROR = "cyberpunk-media/SIGNUP_CLEAR_ERROR"
const UPDATE = "cyberpunk-media/UPDATE"
const VIEWER_REFRESH = "cyberpunk-media/VIEWER_REFRESH"
// Cross-duck: dropped caches on sign-out so the next account never sees
// the previous one's lists (grid, cells, users). Handled here and in the
// grid/cell ducks.
export const RESET_CACHES = "cyberpunk-media/RESET_CACHES"
const ERROR = "cyberpunk-media/ERROR"
const ISLOADING = "cyberpunk-media/ISLOADING"
const AUTH = "cyberpunk-media/AUTH"
const LOADED_USER = "cyberpunk-media/LOADED_USER"
const LOADED_USERS = "cyberpunk-media/LOADED_USERS"
const DELETE_USER = "cyberpunk-media/DELETE_USER"
const DELETE_USERS = "cyberpunk-media/DELETE_USERS"

const INITIAL_STATE = {
  isSignedIn: null,
  loaded: {},
  deletedUser: false,
  loadedUsers: {
    users: {}
  },
  loadedUser: {},
  isError: false,
  isSigningUp: false,
  signupError: null
}

const removeUsersFromRoster = (loadedUsers, userIds) => {
  const users = loadedUsers && loadedUsers.users
  if (!Array.isArray(users)) return loadedUsers

  const removedIds = new Set((userIds || []).map(String))
  const remainingUsers = users.filter(user => !removedIds.has(String(user._id)))
  const removedCount = users.length - remainingUsers.length

  return {
    ...loadedUsers,
    users: remainingUsers,
    total: Math.max((loadedUsers.total || users.length) - removedCount, 0)
  }
}

// Reducer
export const userReducer = (state = INITIAL_STATE, action = {}) => {
  switch (action.type) {
    case AUTH:
      return {
        ...state,
        deletedUser: false,
        isAuth: action.payload
      }
    case LOADED_USER:
      return {
        ...state,
        deletedUser: false,
        loadedUser: action.payload
      }
    case DELETE_USER:
      return {
        ...state,
        deletedUser: !!action.payload.deletingSelf,
        loadedUser: action.payload.deletedUserIds
          .map(String)
          .includes(String(state.loadedUser && state.loadedUser._id))
          ? {}
          : state.loadedUser,
        loadedUsers: removeUsersFromRoster(
          state.loadedUsers,
          action.payload.deletedUserIds
        )
      }

    case DELETE_USERS:
      return {
        ...state,
        deletedUser: false,
        loadedUsers: removeUsersFromRoster(
          state.loadedUsers,
          action.payload.deletedUserIds
        )
      }

    case LOADED_USERS:
      return {
        ...state,
        deletedUser: false,
        loadedUsers: action.payload
      }
    case SIGNUP:
      return {
        ...state,
        isError: false,
        isSigningUp: false,
        signupError: null,
        deletedUser: false,
        loaded: action.payload
      }

    case SIGNUP_REQUEST:
      return {
        ...state,
        isSigningUp: true,
        signupError: null
      }

    case SIGNUP_ERROR:
      return {
        ...state,
        isSigningUp: false,
        signupError: action.payload
      }

    case SIGNUP_CLEAR_ERROR:
      return {
        ...state,
        signupError: null
      }

    case UPDATE:
      return {
        ...state,
        isError: false,
        deletedUser: false,
        loadedUser: action.payload.user
      }
    case VIEWER_REFRESH:
      // Merge fresh identity fields (avatar, name…) into the signed-in
      // viewer blob — it was captured at login and goes stale otherwise.
      return {
        ...state,
        loaded: {
          ...state.loaded,
          user: { ...(state.loaded && state.loaded.user), ...action.payload }
        }
      }
    case RESET_CACHES:
      return {
        ...state,
        loadedUsers: INITIAL_STATE.loadedUsers,
        loadedUser: {}
      }
    case ERROR:
      return {
        ...state,
        isError: true
      }
    case ISLOADING:
      return {
        ...state,
        isLoading: action.status
      }
    default:
      return state
  }
}

// Action Creators
export const signUp = (values = {}) => {
  return async dispatch => {
    dispatch({ type: SIGNUP_REQUEST })
    let [err, response] = await to(cyberpunk.post("/signup", values))
    if (err) {
      console.error("%c err", "background: red", err)
      const message =
        (err.response && err.response.data && err.response.data.error) ||
        "Unable to establish registration uplink"
      errorLog(message)
      dispatch({ type: SIGNUP_ERROR, payload: message })
    } else {
      // Backend now auto-issues a token on signup; drop the user
      // straight into the app instead of bouncing them to /signIn.
      await dispatch(updateUser(response.data))
      if (window != null && response.data && response.data.token) {
        localStorage.setItem("jwt", JSON.stringify(response.data))
      }
      history.push("/grid")
    }
  }
}

export const editUserProfile = (values = {}, userId) => {
  const END_POINT = `/user/${userId}`

  const config = {
    headers: { "content-type": "multipart/form-data" }
  }
  return async dispatch => {
    let [err, response] = await to(cyberpunk.put(END_POINT, values, config))
    if (err) {
      console.error("%c err", "background: red", err)
      const safeError = {
        response: {
          data: {},
          ...err
        }
      }
      errorLog(safeError.response.data)
      await dispatch(errorHandling())
    } else {
      await dispatch(updateEditUser(response.data))
      // Editing your own profile changes the signed-in identity too —
      // keep the viewer blob (and localStorage) in step so avatars and
      // names shown for "you" (composer, rails) update immediately.
      try {
        const blob = JSON.parse(window.localStorage.getItem("jwt") || "null")
        const viewerId = blob && blob.user && blob.user._id
        const updated = response.data || {}
        if (viewerId && String(updated._id) === String(viewerId)) {
          const patch = { name: updated.name, avatar: updated.avatar }
          syncViewerToStorage(patch)
          dispatch({ type: VIEWER_REFRESH, payload: patch })
        }
      } catch (e) {
        // storage unavailable — redux state was still updated
      }
      history.push(`/user/${userId}`)
    }
  }
}

export const signOut = () => {
  return async dispatch => {
    dispatch(setLoading(true))
    let [err, response] = await to(cyberpunk.get("/signout"))

    if (err) {
      errorLog(err)
    }

    const safeResponse = {
      data: {},
      ...(response || {})
    }
    await dispatch(updateUser(safeResponse))
    dispatch({ type: RESET_CACHES })
    if (window != null) {
      localStorage.removeItem("jwt")
    }
    dispatch(setLoading(false))
    history.push("/")
    return { ok: !err }
  }
}

export const signIn = (values = {}) => {
  return async dispatch => {
    dispatch(setLoading(true))
    let [err, response] = await to(cyberpunk.post("/signin", values))

    if (err) {
      console.error("%c err", "background: red", err)
      const safeError = {
        response: {
          data: "error",
          ...err
        }
      }
      errorLog(safeError.response.data)
      dispatch(setLoading(false))
      await dispatch(errorHandling())
    } else {
      const safeResponse = {
        data: {},
        ...response
      }
      await dispatch(updateUser(safeResponse.data))
      if (window != null) {
        localStorage.setItem("jwt", JSON.stringify(safeResponse.data))
      }
      dispatch(setLoading(false))
      history.push("/grid")
    }
  }
}

export const socialLogin = values => {
  return async dispatch => {
    dispatch(setLoading(true))
    let [err, response] = await to(cyberpunk.post("/social-login", values))
    if (err) {
      console.error("%c err", "background: red", err)
      const safeError = {
        response: {
          data: "error",
          ...err
        }
      }
      errorLog(safeError.response.data)
      dispatch(setLoading(false))
      await dispatch(errorHandling())
    } else {
      const safeResponse = {
        data: {},
        ...response
      }
      await dispatch(updateUser(safeResponse.data))
      if (window != null) {
        localStorage.setItem("jwt", JSON.stringify(safeResponse.data))
      }
      dispatch(setLoading(false))
      history.push("/grid")
    }
  }
}

const setLoading = state => {
  return { type: ISLOADING, status: state }
}

export const updateUser = data => {
  return { type: SIGNUP, payload: data }
}
export const clearSignupError = () => ({ type: SIGNUP_CLEAR_ERROR })
export const updateEditUser = data => {
  return { type: UPDATE, payload: data }
}

export const errorHandling = () => {
  return { type: ERROR, payload: "error" }
}

const errorLog = error => {
  console.error("%c Error: ", "background: red; color: yellow", error)
}

//utility function to catch errors
const to = promise => {
  return promise
    .then(data => {
      return [null, data]
    })
    .catch(err => [err])
}

export const checkIsAuthenticated = (values = {}) => {
  return async dispatch => {
    const getToken = JSON.parse(localStorage.getItem("jwt"))
    dispatch(isAuthenticated(getToken)) // always dispatch a function
  }
}

export const loadedUser = user => {
  return { type: LOADED_USER, payload: user }
}

export const loadedUsers = users => {
  return { type: LOADED_USERS, payload: users }
}

// Keep the persisted jwt blob's user in step with VIEWER_REFRESH so a
// reload doesn't resurrect the stale identity.
const syncViewerToStorage = patch => {
  try {
    const raw = window.localStorage.getItem("jwt")
    if (!raw) return
    const blob = JSON.parse(raw)
    if (!blob || !blob.user) return
    blob.user = { ...blob.user, ...patch }
    window.localStorage.setItem("jwt", JSON.stringify(blob))
  } catch (e) {
    // corrupted storage — nothing to sync
  }
}

// Re-fetch the signed-in user's own profile and merge the fields the
// login response may not have carried (avatar was added later — old
// sessions have token blobs without it).
export const refreshViewer = userId => {
  const getToken1 = JSON.parse(localStorage.getItem("jwt"))
  const getToken = (getToken1 && getToken1.token) || "noToken"
  cyberpunk.defaults.headers.common = { Authorization: `bearer ${getToken}` }

  return async dispatch => {
    const [err, response] = await to(cyberpunk.get(`/user/${userId}`))
    if (err) {
      return
    }
    const { name, avatar, isAdmin } = response.data || {}
    const patch = { name, avatar, isAdmin: !!isAdmin }
    syncViewerToStorage(patch)
    dispatch({ type: VIEWER_REFRESH, payload: patch })
  }
}

export const getUserInformation = userId => {
  const getToken1 = JSON.parse(localStorage.getItem("jwt"))
  const getToken = (getToken1 && getToken1.token) || "noToken"

  cyberpunk.defaults.headers.common = { Authorization: `bearer ${getToken}` }

  return async dispatch => {
    const response = await cyberpunk.get(`/user/${userId}`)
    dispatch(loadedUser(response.data))
  }
}

export const getUsers = (page = 1, limit = 12) => {
  const getToken1 = JSON.parse(localStorage.getItem("jwt"))
  const getToken = (getToken1 && getToken1.token) || "noToken"

  cyberpunk.defaults.headers.common = { Authorization: `bearer ${getToken}` }

  return async dispatch => {
    const [err, response] = await to(
      cyberpunk.get(`/users?page=${page}&limit=${limit}`)
    )
    if (err) {
      errorLog(err)
      return
    }
    dispatch(loadedUsers(response.data))
  }
}

export const deleteUser = (userId, options = {}) => {
  const getToken1 = JSON.parse(localStorage.getItem("jwt"))
  const getToken = (getToken1 && getToken1.token) || "noToken"

  cyberpunk.defaults.headers.common = { Authorization: `bearer ${getToken}` }

  return async dispatch => {
    const [err, response] = await to(cyberpunk.delete(`/user/${userId}`))
    if (err) {
      const rawMessage =
        (err.response && err.response.data && err.response.data.error) ||
        "Unable to delete user"
      const message =
        typeof rawMessage === "string"
          ? rawMessage
          : rawMessage.message || "Unable to delete user"
      errorLog(message)
      return { ok: false, error: message }
    }

    const deletedId =
      (response.data && response.data.user && response.data.user._id) || userId
    dispatch(
      deleteUserEvent({
        deletingSelf: !!options.deletingSelf,
        deletedUserIds: [String(deletedId)]
      })
    )
    return { ok: true, deletedUserId: String(deletedId) }
  }
}

export const deleteUsers = userIds => {
  const getToken1 = JSON.parse(localStorage.getItem("jwt"))
  const getToken = (getToken1 && getToken1.token) || "noToken"

  cyberpunk.defaults.headers.common = { Authorization: `bearer ${getToken}` }

  return async dispatch => {
    const [err, response] = await to(
      cyberpunk.post("/users/batch-delete", { userIds })
    )
    if (err) {
      const message =
        (err.response && err.response.data && err.response.data.error) ||
        "Unable to delete selected users"
      errorLog(message)
      return { ok: false, error: message }
    }

    const deletedUserIds = response.data.deletedUserIds || []
    dispatch({ type: DELETE_USERS, payload: { deletedUserIds } })
    return {
      ok: true,
      deletedCount: response.data.deletedCount || deletedUserIds.length,
      deletedUserIds
    }
  }
}

export const deleteUserEvent = payload => {
  return { type: DELETE_USER, payload }
}

export const isAuthenticated = token => {
  return { type: AUTH, payload: token }
}

// Helper without redux
export const isAuthenticatedPure = () => {
  if (window == null) {
    return false
  }
  if (localStorage.getItem("jwt")) {
    return { type: AUTH, payload: JSON.parse(localStorage.getItem("jwt")) }
  } else {
    return false
  }
}
